import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {RotateCcw, Search, Trash2, X} from 'lucide-react';
import secureStorage from '../utils/secureStorage';
import {apiDateToWallClock, toLocalDateStr} from '../utils/dateHelpers';
import {api} from '../config/api';
import {Modal, ListLoader, ListEmpty} from '../stock/StockUI';
import {inputClass} from '../ClientsTable/clientFormUI';
import Button from '../components/Common/Button';
import {Segmented} from './CalendarModals';
import DateInput from "../components/Common/DateInput";

// "Corbeille" des RDV : un RDV supprimé n'est qu'un soft-delete côté API, il
// reste en base. Liste ceux supprimés sur la période choisie et permet de les
// restaurer (POST /evenement/{id}/restore) — les tablettes les récupèrent
// d'elles-mêmes au prochain pull.

const PERIODS = [
    {days: 7, label: '7 jours'},
    {days: 30, label: '30 jours'},
    {days: 90, label: '90 jours'},
    {days: 365, label: '1 an'},
];

const formatDateTime = (date) => {
    if (!date || isNaN(date.getTime())) return '—';
    return date.toLocaleString('fr-FR', {
        weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const formatDuration = (hours) => {
    if (!hours) return '';
    const total = Math.round(hours * 60);
    const h = Math.floor(total / 60);
    const m = total % 60;
    return h ? `${h}h${m ? String(m).padStart(2, '0') : ''}` : `${m} min`;
};

const formatDay = (key) => {
    if (!key) return 'Sans date';
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
};

const DeletedEventsModal = ({onClose, onRestored}) => {
    const [days, setDays] = useState(30);
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [restoringId, setRestoringId] = useState(null);
    const [filterDay, setFilterDay] = useState(''); // 'YYYY-MM-DD' of the RDV, '' = all

    // Grouped by RDV day (salon wall clock), most recent day first, by start
    // time inside a day — filtered on one RDV day when a date is picked.
    const sections = useMemo(() => {
        const byDay = new Map();
        for (const item of items) {
            const start = item.start ? apiDateToWallClock(item.start) : null;
            const key = start && !isNaN(start.getTime()) ? toLocalDateStr(start) : '';
            if (filterDay && key !== filterDay) continue;
            if (!byDay.has(key)) byDay.set(key, []);
            byDay.get(key).push({item, start});
        }
        return [...byDay.entries()]
            .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : b.localeCompare(a)))
            .map(([key, rows]) => ({
                key,
                rows: rows.sort((x, y) => (x.start?.getTime() || 0) - (y.start?.getTime() || 0)),
            }));
    }, [items, filterDay]);
    const shownCount = sections.reduce((n, sec) => n + sec.rows.length, 0);

    // Debounce the search box so typing doesn't fire one request per key.
    useEffect(() => {
        const t = setTimeout(() => setQuery(search.trim()), 350);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const account = await secureStorage.getItem('selectedAccount');
            const response = await api.get('/evenements/deleted', {
                headers: {'X-account-id': account.id, Accept: 'application/json'},
                params: {days, ...(query ? {q: query} : {})},
            });
            setItems(response.data?.items || []);
        } catch (e) {
            console.error('Failed to load deleted events:', e);
            setError(e.response?.data?.error || 'Impossible de charger les RDV supprimés');
        } finally {
            setLoading(false);
        }
    }, [days, query]);

    useEffect(() => {
        load();
    }, [load]);

    const handleRestore = async (item) => {
        setRestoringId(item.id);
        setError('');
        try {
            const account = await secureStorage.getItem('selectedAccount');
            await api.post(`/evenement/${item.id}/restore`, null, {
                headers: {'X-account-id': account.id, Accept: 'application/json'},
            });
            setItems(prev => prev.filter(i => i.id !== item.id));
            onRestored?.(item);
        } catch (e) {
            console.error('Failed to restore event:', e);
            setError(e.response?.data?.error || 'La restauration a échoué');
        } finally {
            setRestoringId(null);
        }
    };

    return (
        <Modal
            title="RDV supprimés"
            subtitle={loading ? undefined : `${shownCount} rendez-vous`}
            onClose={onClose}
            size="lg"
            footer={(
                <div className="border-t border-gray-200 dark:border-gray-700">
                    <button type="button" onClick={onClose}
                            className="w-full py-4 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                        Fermer
                    </button>
                </div>
            )}
        >
            {/* Filters */}
            <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"/>
                        <input
                            type="text"
                            aria-label="Rechercher"
                            placeholder="Client, téléphone, service…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className={`${inputClass(false)} pl-9`}
                        />
                    </div>
                    <div className="relative sm:w-48">
                        <DateInput
                            clearable={false}
                            value={filterDay}
                            onChange={e => setFilterDay(e.target.value)}
                            title="Date du RDV"
                            aria-label="Date du RDV"
                            className={`${inputClass(false)} ${filterDay ? 'pr-9' : ''}`}
                        />
                        {filterDay && (
                            <button onClick={() => setFilterDay('')} title="Toutes les dates" aria-label="Toutes les dates"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                                <X className="w-4 h-4 text-gray-500"/>
                            </button>
                        )}
                    </div>
                </div>
                <Segmented
                    options={PERIODS.map(p => ({value: p.days, label: p.label}))}
                    value={days}
                    onChange={setDays}
                    columns="grid-cols-4"
                />
            </div>

            {error && (
                <div className="mt-3 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 text-sm border border-red-200 dark:border-red-800">
                    {error}
                </div>
            )}

            {/* List */}
            {loading ? (
                <ListLoader label="Chargement…"/>
            ) : shownCount === 0 ? (
                <ListEmpty icon={Trash2} title="Aucun RDV supprimé" text="Aucun rendez-vous supprimé sur cette période."/>
            ) : (
                sections.map(section => (
                    <div key={section.key || 'none'} className="mt-4">
                        <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                            <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                                {formatDay(section.key)}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{section.rows.length}</span>
                        </div>
                        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                            {section.rows.map(({item, start}) => {
                                const deletedAt = item.deletedAt ? new Date(item.deletedAt) : null;
                                const clientName = item.client?.name || item.title || 'Sans client';
                                return (
                                    <li key={item.id} className="py-3 flex items-center gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                                {clientName}
                                                {item.client?.phone && (
                                                    <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">{item.client.phone}</span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-600 dark:text-gray-300 truncate">
                                                {start ? start.toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'}) : '—'}
                                                {item.duration ? ` · ${formatDuration(item.duration)}` : ''}
                                                {item.service ? ` · ${item.service}` : ''}
                                                {item.employee?.name ? ` · ${item.employee.name}` : ''}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                Supprimé le {formatDateTime(deletedAt)}
                                                {item.deletedBy ? ` par ${item.deletedBy}` : ''}
                                            </div>
                                        </div>
                                        <Button variant="tinted" size="sm" icon={RotateCcw}
                                                loading={restoringId === item.id}
                                                disabled={restoringId !== null}
                                                onClick={() => handleRestore(item)}>
                                            Restaurer
                                        </Button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))
            )}
        </Modal>
    );
};

export default DeletedEventsModal;
